import { Calendar } from 'lucide-react';
import { toast } from 'react-toastify';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ProjectForm from '../../components/projects-component/ProjectForm';
import { useWallet } from '../../context/WalletContext';
import { projectAPI } from '../../services/api';
import skyLoader from "../../assets/home-assets/skyLoader.gif";

function Projects() {
  const [showForm, setShowForm] = useState(false);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const { walletAddress, connectWallet } = useWallet();
  const fetchedRef = useRef(false);

  const fetchProjects = async () => {
    try {
      const response = await projectAPI.getAllPublic();
      setProjects(response.data);
      setLoading(false);
      // toast.success('Projects loaded successfully!');
    } catch (err) {
      setError(err.message);
      setLoading(false);
      toast.error(`Something went wrong: ${err.message}`);
    }
  };

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    fetchProjects();
  }, []);

  const filteredProjects = projects.filter((project) =>
    project.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleProjectClick = (project) => {
    //navigate(`/tasks?id=${project?.projectTokenId}`);
    if (project.projectTokenId) {
      navigate(`/projects/token/${project.projectTokenId}`);
    } else {
      toast.error("Project missing projectTokenId:", project);
    }
  };

  const truncateDescription = (text, maxLength = 120) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength) + '...';
  };

  const handleCreateProject = () => {
    if (!walletAddress) {
      toast.warn('Please connect your wallet to create a project');
      connectWallet();
      return;
    }
    navigate('/projects/create');
  };

  if (loading)
    return (
      <div className="flex justify-center items-center min-h-screen ">
          <img 
            src={skyLoader} 
            alt="Loading..." 
            className="w-100 h-100 object-contain"
          />
        </div>
    );

  if (error)
    return <div className='min-h-screen p-8 text-red-500'>Error: {error}</div>;

  return (
    <div className='min-h-screen p-8 bg-gradient-to-b from-indigo-50 via-white to-indigo-100'>
      {/* Header */}
      <div className='flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-10'>
        {/* Title + Subtitle */}
        <div>
          <h1 className='text-2xl font-bold text-black'>Projects</h1>
          <p className='text-gray-500 mt-1 text-base font-medium'>
            Explore innovation opportunities and invest in groundbreaking ideas
          </p>
        </div>

        {/* Button */}
        <button
          onClick={handleCreateProject}
          className={`w-full sm:w-auto px-6 py-2 rounded-lg font-semibold transition-all ${
            !walletAddress
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white shadow-md hover:shadow-lg'
          }`}
        >
          Create Project
        </button>
      </div>

      <div className='mb-6'>
        <input
          type='text'
          placeholder='Search projects by title...'
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className='w-full  px-4 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-400'
        />
      </div>

      {/* Projects Grid */}
      <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6 sm:gap-8'>
        {filteredProjects?.length === 0 ? (
          <div className='col-span-full text-center text-red-400 font-medium py-24'>
            No Projects Found Matching
            <span className='text-indigo-600'>{searchQuery}</span>"
          </div>
        ) : (
          filteredProjects?.map((project) => (
            <div
              key={project?._id}
              className='flex flex-col justify-between h-full bg-gradient-to-br from-white via-indigo-100 to-purple-100 rounded-2xl shadow-md hover:shadow-2xl p-6 transform hover:scale-105 transition-all duration-300'
            >
              {/* Card Header */}
              <div className='border-b border-gray-100 mb-4'>
                <div className='flex justify-between items-start'>
                  <h2 className='text-xl font-medium text-gray-800 mb-2 line-clamp-2'>
                    {project.title}
                  </h2>
                  {/* <div className='flex items-center text-gray-400 text-xs'>
                    <Eye size={16} className='mr-1' />
                    <span>{project.views || 0}</span>
                  </div> */}
                </div>

                <div className='flex flex-wrap gap-2 mt-2'>
                  <span className='bg-indigo-100 text-indigo-700 text-xs px-3 py-1 rounded-full font-medium'>
                    {project.primaryDomain || project.industry}
                  </span>

                  {project.subdomains?.slice(0, 2).map((subdomain, index) => (
                    <span
                      key={index}
                      className='bg-purple-100 text-purple-700 text-xs px-3 py-1 rounded-full font-medium'
                    >
                      {subdomain}
                    </span>
                  ))}
                </div>
              </div>

              {/* Card Body */}
              <div className='flex-grow flex flex-col justify-between'>
                <p className='text-gray-600 text-sm font-medium mb-6'>
                  {truncateDescription(
                    project.shortDescription || project.description
                  )}
                </p>

                <div className='flex flex-wrap justify-between text-xs text-gray-500 mb-4'>
                  <div className='flex items-center'>
                    <Calendar size={14} className='mr-1' />
                    <span className='font-medium'>
                      {new Date(project.createdAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })}
                    </span>
                  </div>

                  {/* <div className='flex items-center'>
                    <Users size={14} className='mr-1' />
                    <span className='font-medium'>
                      {project.contributors?.length || 0} contributors
                    </span>
                  </div> */}
                </div>

                {project.totalInnovationUnits && (
                  <div className='flex items-center justify-between text-xs mb-4 bg-indigo-50 p-3 rounded-lg'>
                    <div>
                      <span className='text-gray-500 font-medium'>
                        Innovation Units:
                      </span>
                      <span className='font-semibold text-indigo-800 ml-1'>
                        {project.totalInnovationUnits}
                      </span>
                    </div>
                    <div>
                      <span className='text-gray-500 font-medium'>
                        Price/IU:
                      </span>
                      <span className='font-semibold text-indigo-800 ml-1'>
                        {project.pricePerUnit} CEL
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Card Footer */}
              <div className='mt-auto pt-4 border-t border-gray-200'>
                <button
                  onClick={() => handleProjectClick(project)}
                  className='w-full block text-center bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white font-semibold py-2 px-6 rounded-lg transition-all duration-300 shadow-sm hover:shadow-md'
                >
                  View Details
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Project Creation Modal */}
      {showForm && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
          <div className='bg-white rounded-xl p-8 w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-xl'>
            <div className='flex justify-between items-center mb-6'>
              <h2 className='text-2xl font-bold text-indigo-700'>
                Create New Project
              </h2>
              <button
                onClick={() => setShowForm(false)}
                className='text-gray-400 hover:text-gray-600 text-2xl'
              >
                ×
              </button>
            </div>
            <ProjectForm
              onSubmitSuccess={() => {
                setShowForm(false);
                fetchProjects();
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default Projects;
